import type { LabelBundle, PackingInvoice } from '@/infrastructure/FulfillmentAdminClient';
import type { PrintJob, PrintOutcome } from '@/models/FulfillmentPrint';
import type { PrintDispatcher } from './PrintDispatcher';
import { PackingSheet } from './PackingSheet';
import {
  buildPaperworkPdf,
  packingDrawing,
  waybillDrawing,
  type PaperworkBuilder,
  type PaperworkPart,
  type PartOutcome,
} from './parcelPdf';

export const carrierLabelJobKey = (orderId: number, trackingNumber: string): string =>
  `carrierLabel:${orderId}:${trackingNumber}`;

export const packingInvoiceJobKey = (orderId: number): string => `packingInvoice:${orderId}`;

/** One parcel whose label the carrier issued, read for a print: the archived pdf and the PACKING sheet behind it. */
export interface ArchivedParcel {
  orderId: number;
  trackingNumber: string;
  pdf: Blob;
  /** Null when the packing invoice could not be read; the label then prints alone and the toast names the order. */
  invoice: PackingInvoice | null;
}

/** One parcel whose Deleo-family sticker this Admin draws, with the PACKING sheet behind it. */
export interface DrawnParcel {
  orderId: number;
  trackingNumber: string;
  /** The sticker's markup, read off the hidden sheet the admin renders for it. */
  waybill: string;
  invoice: PackingInvoice | null;
}

export type Parcel = ArchivedParcel | DrawnParcel;

export const isDrawnParcel = (parcel: Parcel): parcel is DrawnParcel => 'waybill' in parcel;

/**
 * What one parcel's print came back with. The click is one document, so every parcel in it carries the
 * document's outcome as its `label`; `packing` is there only when the parcel's PACKING sheets could not go
 * in, with the reason. `sheetSent` is false when no PACKING sheet went out behind the label at all.
 */
export interface ParcelPrintOutcome {
  orderId: number;
  label: PrintOutcome;
  packing?: PrintOutcome;
  /** Whether a PACKING sheet went out behind the label at all. */
  sheetSent: boolean;
}

/** The PACKING sheets drawn for an invoice, or why they could not be: no pages to print, or a sheet the renderer refused. */
export type PackingSheetsBuild = { sheets: string[]; reason?: undefined } | { sheets: null; reason: string };

/** The PACKING sheet as a print job, or why there is none. */
export type PackingSheetBuild = { job: PrintJob; reason?: undefined } | { job: null; reason: string };

/**
 * The PACKING sheets for a parcel, the ones View Orders prints [SSD-7207]. An invoice with no pages has nothing
 * to put on paper and is said so; a renderer failure keeps its own words, so it is never read as "no data".
 */
export const packingSheetsOf = (invoice: PackingInvoice): PackingSheetsBuild => {
  if (invoice.pages.length === 0) {
    return { sheets: null, reason: `The packing invoice for order ${invoice.orderId} has no pages to print.` };
  }
  try {
    return { sheets: PackingSheet.sheetsOf(invoice) };
  } catch (cause) {
    return {
      sheets: null,
      reason: `The PACKING sheet could not be drawn: ${cause instanceof Error ? cause.message : String(cause)}`,
    };
  }
};

/** The PACKING sheet as a document of its own — what View Orders and Order Detail print by hand — or why it could not be drawn. */
export const packingSheetJob = (invoice: PackingInvoice): PackingSheetBuild => {
  const built = packingSheetsOf(invoice);
  return built.sheets
    ? {
        job: {
          surface: 'packingInvoice',
          jobKey: packingInvoiceJobKey(invoice.orderId),
          role: 'label',
          format: 'html',
          html: PackingSheet.documentOf(invoice, built.sheets),
        },
      }
    : { job: null, reason: built.reason };
};

/**
 * The archive's 404 for a single order, in its own words ("No archived label for any of orders …"): it holds no
 * mergeable pdf for it. A 404 that says anything else — a route that is not there, a proxy — is a read that failed.
 */
export const archiveHasNothing = (cause: unknown): boolean => {
  const response = (cause as { response?: { status?: unknown; data?: unknown } } | null)?.response;
  return response?.status === 404 && /no archived label/i.test(messageOf(response.data));
};

/** The raw bytes of a response body, whatever realm the buffer came from; null when the body is not bytes. */
const bytesOf = (data: unknown): Uint8Array | null => {
  if (ArrayBuffer.isView(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  }
  if (Object.prototype.toString.call(data) === '[object ArrayBuffer]') {
    return new Uint8Array(data as ArrayBuffer);
  }
  return null;
};

/** The archive's words, whichever way they arrived: a parsed body, a string, or the bytes a pdf request answers with. */
const messageOf = (data: unknown): string => {
  let body: unknown = data;
  const bytes = bytesOf(body);
  if (bytes) {
    // The archive's message is ascii json; a decoder is used when the runtime has one, char codes otherwise.
    body =
      typeof TextDecoder === 'undefined'
        ? String.fromCharCode(...bytes.subarray(0, 4096))
        : new TextDecoder().decode(bytes);
  }
  if (typeof body === 'string') {
    const text = body;
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
  return String((body as { message?: unknown } | null)?.message ?? '');
};

/** A label's archive answer and PACKING invoice, read together; skipped when the archive had nothing for the order, failed when it could not answer. */
interface ParcelRead {
  parcel: ArchivedParcel | null;
  skipped: boolean;
  failed?: string;
}

export interface ParcelReadFailure {
  orderId: number;
  reason: string;
}

/**
 * Reads the labels' parcels a few at a time — each order's archive pdf and PACKING invoice side by side — and
 * hands them back in the order given, so a bulk of fifty is not fifty round trips in a row [SSD-7207]. A label
 * the archive cannot read is named with the archive's reason and the other parcels still print.
 */
export const readParcels = async (
  labels: Array<{ orderId: number; trackingNumber: string }>,
  readBundle: (orderIds: number[]) => Promise<LabelBundle>,
  readInvoice: (orderId: number) => Promise<PackingInvoice | null>,
  concurrency = 4,
): Promise<{ parcels: ArchivedParcel[]; skipped: number[]; failed: ParcelReadFailure[] }> => {
  const reads: ParcelRead[] = new Array<ParcelRead>(labels.length);
  let next = 0;
  const worker = async () => {
    while (next < labels.length) {
      const index = next++;
      const label = labels[index]!;
      try {
        const [bundle, invoice] = await Promise.all([readBundle([label.orderId]), readInvoice(label.orderId)]);
        if (bundle.skipped.some((orderId) => orderId !== label.orderId)) {
          throw new Error('The archive answered for an order that was not asked about.');
        }
        reads[index] = bundle.skipped.includes(label.orderId)
          ? { parcel: null, skipped: true }
          : {
              parcel: { orderId: label.orderId, trackingNumber: label.trackingNumber, pdf: bundle.pdf, invoice },
              skipped: false,
            };
      } catch (cause) {
        // Asked for one order, the archive answers 404 when it holds no pdf it can merge for it (a ZPL waybill,
        // say) — the same "nothing for this order" a bundle of several reports as skipped, not a failure.
        reads[index] = archiveHasNothing(cause)
          ? { parcel: null, skipped: true }
          : {
              parcel: null,
              skipped: false,
              failed: `The carrier label could not be read${cause instanceof Error && cause.message ? ` — ${cause.message}` : ''}`,
            };
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, labels.length) }, worker));
  return {
    parcels: reads.flatMap((read) => (read.parcel ? [read.parcel] : [])),
    skipped: labels.filter((_, index) => reads[index]?.skipped).map((label) => label.orderId),
    failed: labels.flatMap((label, index) =>
      reads[index]?.failed ? [{ orderId: label.orderId, reason: reads[index]!.failed! }] : [],
    ),
  };
};

/** One parcel as it goes into the document: its part, or why it has none; and why its sheets are not there, when they are not. */
interface PlannedParcel {
  parcel: Parcel;
  part: PaperworkPart | null;
  refusal?: string;
  sheetRefusal?: string;
}

const planParcel = (parcel: Parcel): PlannedParcel => {
  const built = parcel.invoice ? packingSheetsOf(parcel.invoice) : null;
  const sheets = built?.sheets ? built.sheets.map(packingDrawing) : [];
  const sheetRefusal = built && !built.sheets ? built.reason : undefined;
  if (isDrawnParcel(parcel)) {
    if (!parcel.waybill.trim()) {
      return { parcel, part: null, refusal: 'The waybill sheet was not drawn.', ...(sheetRefusal && { sheetRefusal }) };
    }
    return {
      parcel,
      part: { orderId: parcel.orderId, pages: [waybillDrawing(parcel.waybill)], sheets },
      ...(sheetRefusal && { sheetRefusal }),
    };
  }
  return {
    parcel,
    part: { orderId: parcel.orderId, pdf: parcel.pdf, pages: [], sheets },
    ...(sheetRefusal && { sheetRefusal }),
  };
};

const refused = (parcel: Parcel, reason: string): ParcelPrintOutcome => ({
  orderId: parcel.orderId,
  label: { ok: false, printer: 'label', reason },
  sheetSent: false,
});

/**
 * Prints a click's paperwork as one document, in the order the parcels were given: each carrier label — the
 * archive's pdf, or the waybill this admin drew — with its PACKING sheets laid behind it, so a click of three
 * is one pdf, one print [SSD-7207, 10/13]. A parcel whose label cannot be laid in is refused on its
 * own and the others still print; a parcel whose sheets cannot be drawn keeps its label and says so.
 */
export const printPaperwork = async (
  printer: Pick<PrintDispatcher, 'dispatch'>,
  parcels: Parcel[],
  build: PaperworkBuilder = buildPaperworkPdf,
): Promise<ParcelPrintOutcome[]> => {
  const planned = parcels.map(planParcel);
  const parts = planned.flatMap((plan) => (plan.part ? [plan.part] : []));
  const built = parts.length > 0 ? await build(parts) : { pdf: null, parts: [] };
  let at = 0;
  const fared: Array<PartOutcome | undefined> = planned.map((plan) => (plan.part ? built.parts[at++] : undefined));
  const included = planned.filter((plan, index) => plan.part && fared[index] && !fared[index]!.failure);
  if (!built.pdf || included.length === 0) {
    return planned.map((plan, index) =>
      refused(plan.parcel, plan.refusal ?? fared[index]?.failure ?? 'Nothing of this parcel could be printed.'),
    );
  }
  const printed = included.map((plan) => plan.parcel);
  const outcome = await printer.dispatch({
    surface: 'carrierLabel',
    jobKey:
      printed.length === 1
        ? carrierLabelJobKey(printed[0]!.orderId, printed[0]!.trackingNumber)
        : `carrierLabel:${printed.map((parcel) => parcel.orderId).join(',')}`,
    role: 'label',
    format: 'pdf',
    pdfBlob: built.pdf,
  });
  return planned.map((plan, index) => {
    const part = fared[index];
    if (!plan.part || !part || part.failure) {
      return refused(plan.parcel, plan.refusal ?? part?.failure ?? 'Nothing of this parcel could be printed.');
    }
    const result: ParcelPrintOutcome = { orderId: plan.parcel.orderId, label: outcome, sheetSent: part.sheetPages > 0 };
    const sheetProblem = plan.sheetRefusal ?? part.sheetFailure;
    if (sheetProblem) {
      // A sheet that could not be drawn is a refusal with its own words, not a missing invoice.
      result.packing = { ok: false, printer: 'label', reason: sheetProblem };
    }
    return result;
  });
};

/** The same print under the name the single-parcel screens call it by. */
export const printParcels = printPaperwork;

/** Said after a label print whose PACKING sheets did not all go: which orders, and where to print the sheet by hand. */
export const packingNotPrintedNote = (orderIds: number[], where: string): string =>
  orderIds.length === 0
    ? ''
    : `PACKING sheet not printed for order${orderIds.length === 1 ? '' : 's'} ${orderIds.join(', ')} — packing invoice unavailable; print it from ${where}.`;

/** Every sheet outcome of the parcels, in the order they went out. */
export const sheetOutcomesOf = (results: ParcelPrintOutcome[]): PrintOutcome[] =>
  results.flatMap((result) => (result.packing ? [result.label, result.packing] : [result.label]));
